import { useEffect, useState, useSyncExternalStore } from 'react'
import { STORAGE_KEYS } from '@/config/constants'

type Theme = 'light' | 'dark' | 'system'

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(STORAGE_KEYS.theme)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  return 'light'
}

// Check if dark class is on document
function getIsDark(): boolean {
  if (typeof window === 'undefined') return false
  return document.documentElement.classList.contains('dark')
}

// Subscribe to class changes on documentElement
function subscribeToThemeChanges(callback: () => void): () => void {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName === 'class') {
        callback()
      }
    }
  })

  observer.observe(document.documentElement, { attributes: true })

  return () => {
    observer.disconnect()
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme)

  // Use useSyncExternalStore to reactively track dark class
  const isDark = useSyncExternalStore(subscribeToThemeChanges, getIsDark, () => false)

  const resolvedTheme = isDark ? 'dark' : 'light'

  useEffect(() => {
    const root = document.documentElement
    const currentTheme = theme === 'system' ? getSystemTheme() : theme

    // Eliminar clases anteriores
    root.classList.remove('light', 'dark')

    // Aplicar tema actual
    root.classList.add(currentTheme)

    // Guardar preferencia
    localStorage.setItem(STORAGE_KEYS.theme, theme)
  }, [theme])

  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      const root = document.documentElement
      root.classList.remove('light', 'dark')
      root.classList.add(getSystemTheme())
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
  }

  const toggleTheme = () => {
    const current = isDark ? 'dark' : 'light'
    setTheme(current === 'dark' ? 'light' : 'dark')
  }

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark,
  }
}
